import React, { useState, useEffect } from 'react';
import { 
    Avatar,
    Box, 
    Typography, 
    Paper, 
    Grid, 
    Card, 
    CardContent, 
    Button,
    Divider
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { Crumbs } from '../Crumbs.jsx';
import ReactMarkdown from 'react-markdown';

const ElliesGuide = () => {
    const title = "Ellie's Guide to Two-Step Dancing";
    const desc = "Your comprehensive guide to two-step dancing, from where to dance out of town to essential shopping tips.";
    
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch page data from API
    useEffect(() => {
        const fetchPageData = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${process.env.REACT_APP_BACKEND_HOST}/ellies-page?populate=user_profile.pic`);
                if (!response.ok) {
                    throw new Error('Failed to fetch page data');
                }
                const data = await response.json();
                setPageData(data.data);
            } catch (error) {
                console.error('Error fetching page data:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPageData();
    }, []);

    // Get markdown content from API data or use fallback
    const markdownContent = pageData?.body || `
# Welcome to Ellie's Two-Step Guide

This is your go-to resource for everything two-step dancing! Whether you're planning a trip or looking to upgrade your dance wardrobe, this guide has you covered.

## Loading Content...

Content is being loaded from the server. Please check back soon!
    `;


    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Helmet>
                    <meta charSet="utf-8"/>
                    <meta property="og:description" content={desc}/>
                    <meta property="twitter:title" content={title}/>
                    <meta property="twitter:description" content={desc}/>
                    <title>{title}</title>
                    <meta property="description" content={desc} />
                    <link rel="canonical" href="https://austin2step.com/ellies-guide"/>
                </Helmet>
                <Crumbs crumbs={[
                    {title: "Community"},
                    {title: "Ellie's Guide"}
                ]} />
                <Paper>
                    <Box p={2}>
                        
                        <Typography variant="h4" color="primary" gutterBottom>
                            {title}
                        </Typography>
                        <Box style={{float: "left", marginRight: 16}}>
                            <Avatar src={"/images/ellie.jpeg"} alt="Ellie" sx={{ width: 56, height: 56, mr: 2 }}/>
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            {desc}
                        </Typography>
                    </Box>
                </Paper>
            </Grid>

            {/* Quick Links Section */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" gutterBottom>
                            Quick Links
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Dancing Out of Town
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Discover the best two-step venues and dance halls across Texas and beyond.
                                        </Typography>
                                        <Button 
                                            variant="outlined" 
                                            href="/where-to-dance/out-of-town"
                                            fullWidth
                                        >
                                            Explore Venues
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Boot Buying Guide
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Complete guide to finding the perfect two-step dancing boots.
                                        </Typography>
                                        <Button 
                                            variant="outlined" 
                                            href="/boot-guide"
                                            fullWidth
                                        >
                                            Boot Guide
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Shopping Guide
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Find the perfect clothes and accessories for two-step dancing.
                                        </Typography>
                                        <Button 
                                            variant="outlined" 
                                            href="/shopping"
                                            fullWidth
                                        >
                                            Shop Smart
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            {/* Main Content Section */}
            <Grid item xs={12}>
                <Card id="boot-guide">
                    <CardContent>
                        <Box sx={{ maxWidth: 800 }}>
                            {loading ? (
                                <Typography>Loading content...</Typography>
                            ) : error ? (
                                <Typography color="error">Error loading content: {error}</Typography>
                            ) : (
                                <ReactMarkdown>{markdownContent}</ReactMarkdown>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default ElliesGuide;